import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { Device } from '../../../domain/Device';
import { PAIRING_SECONDS, type Hub } from '../../../domain/Hub';

const POLL_MS = 3000;

type Phase = 'choose' | 'searching' | 'found' | 'timeout';

// Agregar una estación nueva: el hub entra en modo emparejamiento, la
// estación se une sola y el backend la crea en la cuenta; acá se espera a que
// aparezca para ponerle nombre.
export function useAddStation() {
  const history = useHistory();
  const location = useLocation();
  const { hubs: hubsRepo, devices: devicesRepo } = useRepositories();
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [hubId, setHubId] = useState<string | null>(new URLSearchParams(location.search).get('hub'));
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<Phase>('choose');
  const [secondsLeft, setSecondsLeft] = useState(PAIRING_SECONDS);
  const [found, setFound] = useState<Device | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const known = useRef<Set<string>>(new Set());

  useEffect(() => {
    hubsRepo
      .list()
      .then(({ hubs }) => {
        setHubs(hubs);
        setHubId((current) => current ?? (hubs.length === 1 ? hubs[0].id : null));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'No se pudieron cargar los hubs.'))
      .finally(() => setLoading(false));
  }, [hubsRepo]);

  // Mientras busca: cada pocos segundos, ¿apareció una estación nueva de este hub?
  useEffect(() => {
    if (phase !== 'searching' || !hubId) return;
    const startedAt = Date.now();
    const timer = setInterval(async () => {
      const elapsed = Math.round((Date.now() - startedAt) / 1000);
      setSecondsLeft(Math.max(PAIRING_SECONDS - elapsed, 0));
      try {
        const { devices } = await devicesRepo.list();
        const station = devices.find((d) => d.hubId === hubId && !known.current.has(d.id));
        if (station) {
          setFound(station);
          setName(station.name);
          setPhase('found');
          return;
        }
      } catch {
        // Un error de red suelto no corta la búsqueda.
      }
      if (elapsed >= PAIRING_SECONDS) setPhase('timeout');
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [phase, hubId, devicesRepo]);

  const start = async () => {
    if (!hubId) return;
    setError(null);
    try {
      const { devices } = await devicesRepo.list();
      known.current = new Set(devices.map((d) => d.id));
      const { hubOnline } = await hubsRepo.command(hubId, 'emparejar', PAIRING_SECONDS);
      if (!hubOnline) {
        setError('El hub está sin conexión: no puede buscar estaciones. Revisá que tenga luz e internet.');
        return;
      }
      setSecondsLeft(PAIRING_SECONDS);
      setPhase('searching');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo pedirle al hub que busque estaciones.');
    }
  };

  const save = async () => {
    if (!found) return;
    setSaving(true);
    setError(null);
    try {
      if (name.trim() && name.trim() !== found.name) await devicesRepo.update(found.id, { name: name.trim() });
      history.replace(`/app/stations/${found.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el nombre.');
    } finally {
      setSaving(false);
    }
  };

  return {
    hubs,
    hubId,
    setHubId,
    loading,
    phase,
    secondsLeft,
    found,
    name,
    setName,
    error,
    saving,
    start,
    retry: () => setPhase('choose'),
    save,
    goToClaimHub: () => history.push('/app/hubs/vincular'),
  };
}
